// THE CLINIC'S CALENDAR DAY — the one clock this app judges "today" by.
//
// Owner, 2026-08-17: "every day at 12:01 est it should reset the table…
// weekly should be every Sunday at 12:01 Midnight so Monday… Monthly on the
// 12:01 of the 1st of every month." That rollover is Eastern, always, for
// everyone.
//
// Why this file exists: the client derived "today" from `new Date()` in the
// BROWSER's timezone. The clinics are in Florida, but the owner reads this app
// from seven hours ahead — so at 00:49 on his screen it was still 17:49 the
// PREVIOUS day in Florida. The server (src/task_periods.js, America/New_York)
// correctly answered with the 16th's period while the client called it the
// 17th, so every task rendered OVERDUE against a period that had not ended and
// the completed table headed a different date from the masthead.
//
// This is the client's copy of `clinicDateString` in src/task_periods.js, and
// deliberately the same shape: en-CA renders exactly YYYY-MM-DD, and the result
// is asserted rather than assumed, because a different shape here poisons every
// comparison downstream.

export const CLINIC_TIME_ZONE = 'America/New_York';

/** Today at the clinics, as YYYY-MM-DD. Never the browser's own day. */
export function clinicToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CLINIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(parts)) {
    throw new Error(`clinic date came out as "${parts}", expected YYYY-MM-DD`);
  }
  return parts;
}

/** The clinic's wall-clock date as a local Date at midnight, for the few places
 * that need Date arithmetic (a month name, a weekday). Built from the parts, so
 * it never shifts a day the way `new Date('2026-08-17')` does. */
export function clinicNow(now: Date = new Date()): Date {
  const [y, m, d] = clinicToday(now).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** When a report landed, clinic clock: `04:15am 01/01/26`. Never the browser's zone. */
export function formatReportLanded(input: Date | string): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  }).formatToParts(d);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const hour = pick('hour').padStart(2, '0');
  const ap = pick('dayPeriod').replace(/\./g, '').replace(/\s/g, '').toLowerCase();
  return `${hour}:${pick('minute')}${ap} ${pick('month')}/${pick('day')}/${pick('year')}`;
}

/** Who wrote a note, and when, clinic clock: `04:15am 01/01/26 — tomer@nvmanager.com`. */
export function formatNoteStamp(who: string | null | undefined, when: Date | string): string {
  const person = String(who || '').trim() || 'Unknown';
  const landed = formatReportLanded(when);
  return landed ? `${landed} — ${person}` : person;
}
